import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, Group, Stack } from "@mantine/core";
import { useResetSetting, useUpdateSetting } from "../api/admin";
import { ApiError } from "../api/client";
import type { Setting } from "../api/types";
import { Chip } from "../ui/Chip";
import { alpha, token } from "../ui/tokens";
import { FeedbackLine } from "../ui/FeedbackLine";

// The flat variant of the provider row: a show goes to every enabled destination, so there is
// no rank, nothing to drag, and the row is a single cell.
const TARGET_ROW: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 10,
  alignItems: "center",
  padding: "8px 10px",
  border: "1px solid var(--mantine-color-default-border)",
  borderRadius: "var(--mantine-radius-sm)",
  background: alpha(token.black, 0.04),
  cursor: "default",
};

const LABEL_CELL: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  minWidth: 0,
  cursor: "pointer",
};

/**
 * Where a finished show is posted.
 *
 * A checkbox list rather than the ordered one cover art uses: rank is meaningless for
 * destinations. A show goes to every enabled one, and there is no "first that answers wins"
 * to express.
 *
 * Off by default and deliberately so. Posting to a public channel is not something a
 * deployment should start doing merely because it was upgraded.
 */

// `label` is a proper noun and stays literal; the blurb is copy, so the table holds its
// catalogue key and the render site translates it.
const KNOWN: Record<string, { label: string; blurbKey: string }> = {
  telegram: { label: "Telegram", blurbKey: "publishTargets.blurbTelegram" },
  vk: { label: "VK", blurbKey: "publishTargets.blurbVk" },
};

export interface TargetRow {
  name: string;
  enabled: boolean;
}

export function PublishTargets({ setting }: { setting: Setting }) {
  const { t } = useTranslation("admin");
  const update = useUpdateSetting();
  const reset = useResetSetting();
  // The server is the authority on this value: it completes the list with every target the
  // build knows about, so what comes back from a save is not always what was sent. An
  // in-flight edit is therefore held as an override tagged with the server value it was
  // applied on top of, and is dropped the moment a fresh one arrives.
  const [pending, setPending] = useState<{ base: unknown; rows: TargetRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = pending && pending.base === setting.value ? pending.rows : parseRows(setting.value);

  const save = async (next: TargetRow[]) => {
    const base = setting.value;
    const previous = rows;
    setPending({ base, rows: next }); // optimistic: a checkbox should feel like a switch, not like a form
    setError(null);
    try {
      await update.mutateAsync({ key: setting.key, value: next });
    } catch (caught) {
      setPending({ base, rows: previous });
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    }
  };

  const toggle = (index: number) =>
    void save(rows.map((row, i) => (i === index ? { ...row, enabled: !row.enabled } : row)));

  const active = rows.filter((row) => row.enabled);

  // No rule and no padding of its own: `SettingShell` draws the row this sits inside.
  return (
    <>
      <Stack component="ol" gap={4} style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((row, index) => {
          const meta = KNOWN[row.name];
          return (
            <Box
              component="li"
              key={row.name}
              // Dimmed, not hidden: a disabled destination keeps its place in the list.
              style={row.enabled ? TARGET_ROW : { ...TARGET_ROW, opacity: 0.5 }}
            >
              <Checkbox
                style={LABEL_CELL}
                checked={row.enabled}
                onChange={() => toggle(index)}
                label={meta?.label ?? row.name}
                description={meta ? t(meta.blurbKey) : row.name}
              />
            </Box>
          );
        })}
      </Stack>

      <Group gap={10} mt={8} fz={12} c="dimmed">
        {active.length === 0
          ? t("publishTargets.summaryNone")
          : t("publishTargets.summaryActive", {
              targets: active.map((row) => KNOWN[row.name]?.label ?? row.name).join(", "),
            })}
        {setting.source === "db" && (
          <Chip onClick={() => void reset.mutateAsync(setting.key)}>
            {t("publishTargets.reset")}
          </Chip>
        )}
      </Group>

      <FeedbackLine role="alert">{error}</FeedbackLine>
    </>
  );
}

/** Read the stored value, tolerating a bare-string form and anything unexpected. */
export function parseRows(value: unknown): TargetRow[] {
  if (!Array.isArray(value)) return [];
  const rows: TargetRow[] = [];
  for (const entry of value) {
    if (typeof entry === "string") rows.push({ name: entry, enabled: true });
    else if (entry && typeof entry === "object" && "name" in entry) {
      const row = entry as { name: unknown; enabled?: unknown };
      if (typeof row.name === "string") {
        rows.push({ name: row.name, enabled: row.enabled !== false });
      }
    }
  }
  return rows;
}
